/**
 * SuperUsersTab — all users across all orgs.
 * Navigates to full-page UserEditPage wizard for create/edit.
 */
import React, { useEffect, useState } from 'react';
import {
  Stack, Group, Button, TextInput, Select, Table, Badge, ActionIcon,
  Text, Loader, Alert, Tooltip,
} from '@mantine/core';
import {
  MdEdit, MdPersonAddAlt1, MdSearch, MdRefresh, MdPersonOff,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getUsers, toggleUser, getOrgs } from '../../API/adminAPI';

const ATOMS_BLUE = '#1F4E79';

const roleBadge = {
  atoms_admin:        { color: 'violet', label: 'ATOMS Admin' },
  oem_admin:          { color: 'blue',   label: 'OEM Admin' },
  distributor_admin:  { color: 'teal',   label: 'Dist. Admin' },
  user:               { color: 'gray',   label: 'User' },
};

const SuperUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterOrg, setFilterOrg] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [ud, od] = await Promise.all([getUsers(), getOrgs()]);
      setUsers(ud.users || []);
      setOrgs(
        (od.organisations || []).map((o) => ({ value: String(o.id), label: `${o.name} (${o.org_type})` }))
      );
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (userId, e) => {
    e.stopPropagation();
    try {
      await toggleUser(userId);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Toggle failed');
    }
  };

  const openCreate = () => navigate('/app/users/new');
  const openEdit = (u) => navigate(`/app/users/${u.id}/edit`, { state: { user: u } });

  const orgOptions = [{ value: '', label: 'All Organisations' }, ...orgs];

  const filtered = users.filter((u) => {
    const matchSearch =
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchOrg = !filterOrg || String(u.org_id) === filterOrg;
    return matchSearch && matchOrg;
  });

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <Group spacing="xs" sx={{ flex: 1 }}>
          <TextInput
            placeholder="Search users…"
            icon={<MdSearch />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            sx={{ maxWidth: 240 }}
          />
          <Select
            placeholder="Filter by org"
            data={orgOptions}
            value={filterOrg || ''}
            onChange={(v) => setFilterOrg(v || null)}
            clearable
            sx={{ maxWidth: 240 }}
          />
        </Group>
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle"><MdRefresh size={20} /></ActionIcon>
          <Button
            leftIcon={<MdPersonAddAlt1 />}
            onClick={openCreate}
            sx={{ backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: '#163a5f' } }}
          >
            New User
          </Button>
        </Group>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Organisation</th>
            <th>Role</th>
            <th>Status</th>
            <th style={{ width: 80 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={6}>
              <Text color="dimmed" align="center" py="md">No users found</Text>
            </td></tr>
          ) : filtered.map((u) => {
            const rb = roleBadge[u.role] || roleBadge.user;
            return (
              <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(u)}>
                <td><Text size="sm" weight={500}>{u.username}</Text></td>
                <td><Text size="sm" color="dimmed">{u.email || '—'}</Text></td>
                <td><Text size="sm">{u.org_name || '—'}</Text></td>
                <td><Badge color={rb.color} variant="light" size="sm">{rb.label}</Badge></td>
                <td>
                  <Badge color={u.is_active ? 'green' : 'red'} variant="dot" size="sm">
                    {u.is_active ? 'Active' : 'Suspended'}
                  </Badge>
                </td>
                <td>
                  <Group spacing={4} onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Edit">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(u)}>
                        <MdEdit />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={u.is_active ? 'Suspend' : 'Activate'}>
                      <ActionIcon size="sm" variant="subtle"
                        color={u.is_active ? 'orange' : 'green'}
                        onClick={(e) => handleToggle(u.id, e)}>
                        <MdPersonOff />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Stack>
  );
};

export default SuperUsersTab;
